import React, { useCallback, useEffect, useState } from 'react';
import { FaFilePdf, FaSpinner, FaCheckSquare, FaSquare } from 'react-icons/fa';
import boletasPlanillaApi from '../../services/api/boletasPlanillaApi';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';

const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const GeneracionBoletas = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();

  const [lotes, setLotes] = useState([]);
  const [loteId, setLoteId] = useState('');
  const [registros, setRegistros] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadLotes = useCallback(async () => {
    if (!proyectoId) return;
    const res = await boletasPlanillaApi.getLotes(proyectoId);
    setLotes(res.data?.data || []);
  }, [proyectoId]);

  useEffect(() => { loadLotes(); }, [loadLotes]);

  const loadRegistros = useCallback(async (id) => {
    if (!id) { setRegistros([]); setSelected(new Set()); return; }
    setLoading(true);
    try {
      const res = await boletasPlanillaApi.getRegistrosLote(id);
      const rows = (res.data?.data || []).filter((r) => r.estado === 'ok');
      setRegistros(rows);
      setSelected(new Set());
    } catch (e) {
      setMsg({ type: 'error', text: 'Error al cargar registros' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loteId) loadRegistros(loteId);
  }, [loteId, loadRegistros]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === registros.length) setSelected(new Set());
    else setSelected(new Set(registros.map((r) => r.id)));
  };

  const handleGenerarSeleccion = async () => {
    if (selected.size === 0) {
      setMsg({ type: 'error', text: 'Seleccione al menos un registro' });
      return;
    }
    setGenerating(true);
    try {
      const res = await boletasPlanillaApi.generarPdf([...selected]);
      const isZip = res.headers['content-type']?.includes('zip');
      const name = isZip ? `boletas_${loteId}.zip` : 'boleta.pdf';
      downloadBlob(res.data, name);
      setMsg({ type: 'success', text: `Generado: ${selected.size} boleta(s)` });
      loadRegistros(loteId);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Error al generar PDF' });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerarLoteCompleto = async () => {
    if (!loteId) return;
    setGenerating(true);
    try {
      const res = await boletasPlanillaApi.generarPdfLote(loteId);
      const isZip = res.headers['content-type']?.includes('zip');
      downloadBlob(res.data, isZip ? `boletas_lote_${loteId}.zip` : 'boleta.pdf');
      setMsg({ type: 'success', text: 'Boletas del lote generadas' });
      loadRegistros(loteId);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Error al generar' });
    } finally {
      setGenerating(false);
    }
  };

  if (needsProyectoSelection) {
    return (
      <div className="p-4 dh-reporte-bg">
        <RrhhProyectoSelector
          isCorporativo={isCorporativo}
          proyectos={proyectos}
          proyectoId={proyectoId}
          onChange={setProyectoId}
          loading={loadingProyectos}
        />
        <div className="alert alert-info">Seleccione un proyecto.</div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg" style={{ minHeight: '100vh', padding: '1.5rem' }}>
      <style>{`
        .dh-reporte-bg { background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%); }
        .dh-main-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; padding: 1.5rem; border-radius: 12px; margin-bottom: 1.5rem; }
        .dh-card { background: #fff; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); padding: 1.25rem; }
        .btn-dh-primary { background: linear-gradient(135deg, #11998e, #38ef7d); color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; }
        .btn-dh-secondary { background: #6c757d; color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; }
        .dh-form-control { border-radius: 8px; border: 1px solid #ddd; padding: 0.45rem 0.65rem; width: 100%; }
      `}</style>

      <RrhhProyectoSelector
        isCorporativo={isCorporativo}
        proyectos={proyectos}
        proyectoId={proyectoId}
        onChange={setProyectoId}
        loading={loadingProyectos}
      />

      {msg && (
        <div className={`alert ${msg.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
          {msg.text}
          <button type="button" className="btn-close float-end" onClick={() => setMsg(null)} />
        </div>
      )}

      <div className="dh-main-header d-flex justify-content-between flex-wrap gap-2">
        <div>
          <h1 className="mb-1"><FaFilePdf className="me-2" />Generación de Boletas</h1>
          <p className="mb-0 opacity-75">Seleccione registros de una carga y genere PDF</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn-dh-secondary" onClick={handleGenerarLoteCompleto} disabled={!loteId || generating}>
            {generating ? <FaSpinner className="fa-spin" /> : <FaFilePdf />} Todo el lote
          </button>
          <button type="button" className="btn-dh-primary" onClick={handleGenerarSeleccion} disabled={selected.size === 0 || generating}>
            {generating ? <FaSpinner className="fa-spin" /> : <FaFilePdf />} Seleccionados ({selected.size})
          </button>
        </div>
      </div>

      <div className="dh-card">
        <label className="fw-bold mb-2">Lote de carga</label>
        <select className="dh-form-control mb-3" value={loteId} onChange={(e) => setLoteId(e.target.value)}>
          <option value="">— Seleccione lote —</option>
          {lotes.map((l) => (
            <option key={l.id} value={l.id}>
              #{l.id} — {MESES[l.periodo_mes]} {l.periodo_anio} — {l.homologacion_nombre} ({l.registros_ok} OK)
            </option>
          ))}
        </select>

        {loading ? <FaSpinner className="fa-spin" /> : (
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <button type="button" className="btn btn-sm btn-link p-0" onClick={toggleAll} title="Seleccionar todos">
                    {selected.size === registros.length && registros.length > 0 ? <FaCheckSquare /> : <FaSquare />}
                  </button>
                </th>
                <th>Fila</th><th>DNI</th><th>Trabajador</th><th>Neto</th><th>Generaciones</th><th>Última</th>
              </tr>
            </thead>
            <tbody>
              {registros.length === 0 && <tr><td colSpan={7} className="text-muted">Sin registros válidos en este lote</td></tr>}
              {registros.map((r) => (
                <tr key={r.id} onClick={() => toggle(r.id)} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td>{r.fila_excel}</td>
                  <td>{r.dni}</td>
                  <td>{r.nombres_completos}</td>
                  <td>{r.datos?.neto_pagar}</td>
                  <td>{r.total_generaciones ?? 0}</td>
                  <td className="small">{r.ultima_generacion_at ? String(r.ultima_generacion_at).slice(0, 16) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default GeneracionBoletas;
